import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/*
 * Aclosy cart boost (rendered by snippets/xo-cart-boost.liquid).
 * Thresholds mirror Shopify > Settings > Shipping (USD). Change them here if the rates change.
 */
const FREE_FROM_FIRST_ITEM = ['US', 'GB', 'IE', 'CA']; // free tracked delivery on every order
const NO_FREE_TIER = ['IN']; // flat rate, no free threshold: show the 15% perk only
const FREE_STANDARD_AT = 150; // other countries: free standard delivery from $150

const rate = () => Number(window.Shopify?.currency?.rate) || 1;

/** @param {number} cents @param {string} currency */
function money(cents, currency) {
  const whole = Math.ceil(cents / 100);
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(whole);
  } catch {
    return `${whole} ${currency}`;
  }
}

function readTier() {
  try { return Number(sessionStorage.getItem('xo-ship-tier')) || 0; } catch { return 0; }
}
function writeTier(tier) {
  try { sessionStorage.setItem('xo-ship-tier', String(tier)); } catch {}
}

class XoShipBar extends HTMLElement {
  static observedAttributes = ['data-total', 'data-country'];

  connectedCallback() {
    this.render();
  }

  attributeChangedCallback(_name, oldValue, newValue) {
    if (this.isConnected && oldValue !== newValue) this.render();
  }

  render() {
    const total = Number(this.dataset.total) || 0;
    const country = (this.dataset.country || '').toUpperCase();
    const currency = this.dataset.currency || 'USD';

    // No free-delivery tier (e.g. India): still show the 15% perk.
    if (NO_FREE_TIER.includes(country)) {
      this.innerHTML =
        '<div class="xo-ship__box xo-sage"><p class="xo-sage__h">✓ Your 15% off is applied</p>' +
        '<p class="xo-sage__sub">No code needed, <b>check out securely</b></p></div>';
      return;
    }

    const freeAt = FREE_FROM_FIRST_ITEM.includes(country) ? 0 : FREE_STANDARD_AT * 100 * rate();
    let html, tier;
    if (total < freeAt) {
      const pct = Math.max(4, Math.min(100, Math.round((total / freeAt) * 100)));
      html =
        '<p class="xo-sage__h">✓ Your 15% off is applied</p>' +
        `<p class="xo-sage__sub">You're <b>${money(freeAt - total, currency)}</b> away from <b>free tracked delivery</b></p>` +
        `<div class="xo-sage__track"><div class="xo-sage__fill" style="width:${pct}%"></div></div>`;
      tier = 0;
    } else {
      html =
        '<p class="xo-sage__h">✓ Your perks are applied</p>' +
        '<div class="xo-sage__chips"><span>15% off · auto</span><span>Free tracked delivery</span></div>' +
        '<p class="xo-sage__sub">Your look is ready, <b>check out securely</b></p>';
      tier = 1;
    }

    const pop = tier > readTier();
    writeTier(tier);
    this.innerHTML = `<div class="xo-ship__box xo-sage${pop ? ' xo-ship--pop' : ''}">${html}</div>`;
  }
}

class XoUpsell extends HTMLElement {
  static observedAttributes = ['data-anchor', 'data-in-cart'];

  /** @type {string | undefined} */
  #key;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#load();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  attributeChangedCallback(_name, oldValue, newValue) {
    if (this.isConnected && oldValue !== newValue) this.#load();
  }

  async #load() {
    const anchor = this.dataset.anchor;
    if (!anchor) {
      this.replaceChildren();
      return;
    }
    const key = `${anchor}|${this.dataset.inCart}`;
    if (key === this.#key) return;
    this.#key = key;

    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}recommendations/products?product_id=${encodeURIComponent(anchor)}&limit=10&intent=related&section_id=xo-cart-upsell`;
    try {
      const html = await fetch(url).then((r) => r.text());
      if (key !== this.#key) return;
      const box = new DOMParser().parseFromString(html, 'text/html').querySelector('.xo-up');
      if (!box || !box.querySelector('.xo-up__card')) {
        this.replaceChildren();
        return;
      }
      this.innerHTML = box.innerHTML;
    } catch {
      this.replaceChildren();
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('.xo-up__add') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    const card = button.closest('.xo-up__card');
    const field = card?.querySelector('.xo-up__sel');
    const variantId = field && 'value' in field ? String(field.value) : '';
    if (variantId) this.#add(variantId, button, card?.querySelector('.xo-up__err'));
  };

  /**
   * Adds one unit the same way the theme's product form does, so the drawer, the cart count and
   * Shopify's add-to-cart tracking all update through the theme's own CartLinesUpdateEvent flow.
   * @param {string} variantId @param {HTMLButtonElement} button @param {Element | null | undefined} errorEl
   */
  async #add(variantId, button, errorEl) {
    button.disabled = true;
    button.textContent = 'Adding…';
    if (errorEl) errorEl.hidden = true;

    const sections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el instanceof HTMLElement ? el.dataset.sectionId : '')
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sections.join(',') }),
      }).then((r) => r.json());

      const cart = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json());

      if (response.status) {
        if (errorEl) {
          errorEl.textContent = response.description || response.message || 'Could not add this item.';
          errorEl.hidden = false;
        }
        this.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'xo-upsell', itemCount: 1 },
        });
        button.disabled = false;
        button.textContent = 'Add';
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'xo-upsell', itemCount: 1, sections: response.sections, didError: false },
      });
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Network error', code: 'SERVICE_UNAVAILABLE' }));
      button.disabled = false;
      button.textContent = 'Add';
    }
  }
}

if (!customElements.get('xo-ship-bar')) customElements.define('xo-ship-bar', XoShipBar);
if (!customElements.get('xo-upsell')) customElements.define('xo-upsell', XoUpsell);